/**
 * Returns an object containing the values that have changed between two objects.
 * Uses shallow comparison for primitive values and objects with _id properties.
 *
 * @param {Object} values - The current values object.
 * @param {Object} initialValues - The initial values object.
 * @returns {Object} - An object containing the changed values.
 */
export const getChangedValues = (values, initialValues) => {
  const changedValues = {};
  for (const key in values) {
    if (values[key]?._id) {
      if (values[key]._id !== initialValues[key]) {
        changedValues[key] = values[key]?._id;
      }
    } else {
      if (values[key] !== initialValues[key]) {
        changedValues[key] = values[key];
      }
    }
  }
  return changedValues;
};

/**
 * @param {any} a - First value to compare.
 * @param {any} b - Second value to compare.
 * @returns {boolean} - True if values are deeply equal, false otherwise.
 */
const deepEqual = (a, b) => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }
  if (typeof a === "object" && typeof b === "object") {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    for (const key of keysA) {
      if (!keysB.includes(key) || !deepEqual(a[key], b[key])) return false;
    }
    return true;
  }
  return false;
};

/**
 * Returns an object containing the values that have changed between two objects.
 * Uses deep comparison for complex nested objects and arrays (e.g., Rich Text Editor content).
 *
 * @param {Object} values - The current values object.
 * @param {Object} initialValues - The initial values object.
 * @returns {Object} - An object containing the changed values.
 */
export const getChangedValuesDeep = (values, initialValues) => {
  const changedValues = {};
  for (const key in values) {
    if (!deepEqual(values[key], initialValues[key])) {
      changedValues[key] = values[key];
    }
  }
  return changedValues;
};
